import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

import { getTelegramGroups } from '../../../../api/groups';
import usePreviewFeature from '../../../../hooks/usePreviewFeature';
import './study-group-link.scss';

const TelegramIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M20.7 4.3 3.4 11c-1.2.5-1.2 1.2-.2 1.5l4.4 1.4 1.7 5.2c.2.6.1.8.7.8.5 0 .7-.2 1-.5l2.1-2.1 4.5 3.3c.8.5 1.4.2 1.6-.8l2.9-13.8c.3-1.2-.4-1.8-1.4-1.3Zm-3 3.4-8.3 7.5-.3 3.5-1.4-4.6 9.2-5.8c.4-.3.8-.1.8-.6Z" style={{ fill: 'currentColor', stroke: 'none' }} />
  </svg>
);

const GroupIcon = () => (
  <svg className="sgl-btn__people" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="9" cy="8" r="3.2" style={{ fill: 'none' }} />
    <path d="M3 19.5c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" style={{ fill: 'none' }} />
    <path d="M15.5 4.9a3.2 3.2 0 0 1 0 6.2" style={{ fill: 'none' }} />
    <path d="M17.5 14.3c2.1.6 3.5 2.6 3.5 5.2" style={{ fill: 'none' }} />
  </svg>
);

const BtnIcon = () => (
  <span className="sgl-btn__icon">
    <GroupIcon />
    <span className="sgl-btn__tg"><TelegramIcon /></span>
  </span>
);

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 6l6 6-6 6" style={{ fill: 'none' }} />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6 6 18" style={{ fill: 'none' }} />
  </svg>
);

const StudyGroupLink = ({ token }) => {
  const isEnabled = usePreviewFeature('study-group-link');
  const [groups, setGroups] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!token || !isEnabled) {
      return undefined;
    }

    let alive = true;

    getTelegramGroups({ token })
      .then((response) => {
        if (alive) setGroups(response?.data?.data?.groups || []);
      })
      .catch(() => {
        if (alive) setGroups([]);
      });

    return () => {
      alive = false;
    };
  }, [token, isEnabled]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  if (!isEnabled || groups.length === 0) {
    return null;
  }

  if (groups.length === 1) {
    const [group] = groups;

    return (
      <a
        className="sgl-btn"
        href={group.url}
        target="_blank"
        rel="noopener noreferrer"
        title={`Моя група ${group.title} у Telegram`}
        aria-label={`Моя група ${group.title} у Telegram`}
      >
        <BtnIcon />
        <span className="sgl-btn__text">Моя група</span>
      </a>
    );
  }

  const closeOnOverlay = (event) => {
    if (event.target === event.currentTarget) setIsOpen(false);
  };

  return (
    <>
      <button
        type="button"
        className="sgl-btn"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label="Мої групи в Telegram"
        title="Мої групи в Telegram"
        onClick={() => setIsOpen(true)}
      >
        <BtnIcon />
        <span className="sgl-btn__text">Мої групи</span>
        <span className="sgl-btn__count">{groups.length}</span>
      </button>

      {isOpen && createPortal(
        <div className="sgl-overlay" role="presentation" onClick={closeOnOverlay}>
          <div className="sgl-modal" role="dialog" aria-modal="true" aria-labelledby="sgl-modal-title">
            <div className="sgl-modal__head">
              <h2 className="sgl-modal__title" id="sgl-modal-title">Ваші групи в Telegram</h2>
              <button type="button" className="sgl-modal__close" aria-label="Закрити" onClick={() => setIsOpen(false)}>
                <CloseIcon />
              </button>
            </div>
            <p className="sgl-modal__hint">Оберіть групу — відкриється її чат у Telegram.</p>
            <ul className="sgl-list">
              {groups.map((group) => (
                <li key={group.id}>
                  <a
                    className="sgl-item"
                    href={group.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsOpen(false)}
                  >
                    {group.flag
                      ? <img className="sgl-item__flag" src={group.flag} alt="" width="32" height="21" loading="lazy" />
                      : <span className="sgl-item__flag sgl-item__flag--empty" aria-hidden="true" />}
                    <span className="sgl-item__body">
                      <span className="sgl-item__title">{group.title}</span>
                      {group.direction && <span className="sgl-item__direction">{group.direction}</span>}
                    </span>
                    <span className="sgl-item__arrow"><ArrowIcon /></span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

StudyGroupLink.propTypes = {
  token: PropTypes.string,
};

StudyGroupLink.defaultProps = {
  token: '',
};

export default StudyGroupLink;
